/**
 * App module strings — the unit of entitlement (ADR-0023 §4).
 *
 * `company_entitlements.modules` is a `text[]` per company listing which
 * bounded contexts the tier unlocks. The `entitledProcedureFor(module)` gate
 * (apps/api `trpc.ts`) and the web nav-gating seam both read these strings, so
 * the canonical list lives here — shared by api + web, never duplicated.
 *
 * Garment is simply a bundle of extra module strings on top of the generic
 * ERP (which needs no module to use — generic procedures use `tenantedProcedure`,
 * not the gate). A "Factory" workspace's `modules` array carries the eight
 * `garment.*` strings below; a generic workspace carries none.
 *
 * @see docs/product/garment-os-v1-build-plan.md §0.2 — module entitlements, not a "vertical"
 */
import { z } from "zod";

/**
 * The eight garment bounded contexts gated per workspace. BOM is generated by
 * the costing module (no separate string); the Order Book is a view over
 * `garment.styles`. Mirrors the greenfield contexts in build-plan §2.
 */
export const GARMENT_MODULES = [
  "garment.styles",
  "garment.costing",
  "garment.samples",
  "garment.tna",
  "garment.materials",
  "garment.production",
  "garment.qc",
  "garment.dispatch",
] as const;

export type GarmentModule = (typeof GARMENT_MODULES)[number];

/**
 * Point-of-Sale module(s) — the retail add-on. A single gate string today
 * (`pos.register`) that the POS nav group checks; per-op control stays on the
 * `pos.*` permissions. Like garment, it's added to a workspace's `modules` only
 * when the POS add-on is enabled (`company_addons`).
 */
export const POS_MODULES = ["pos.register"] as const;

export type PosModule = (typeof POS_MODULES)[number];

/**
 * Flint AI module — the assistant add-on. A single gate string that turns ALL
 * AI surfaces on/off (Ask Flint, the chat, channel replies, AI credits, the
 * Flint AI settings page). When the AI add-on is off, none of these appear.
 */
export const AI_MODULES = ["ai.assistant"] as const;

export type AiModule = (typeof AI_MODULES)[number];

/**
 * WhatsApp Business module — the channel add-on. A single gate string that
 * unlocks *business* messaging on WhatsApp (invoices, reminders, briefings, the
 * bot/assistant replies). It gates ONLY engagement sends — NEVER security ones:
 * OTP, step-up codes, and irreversible-action confirmations are platform
 * integrity, route through `messageBus.sendNow` (which this gate never touches),
 * and always reach the user (SMS floor). So a workspace without this add-on can
 * still log in and verify; it just can't send business messages on WhatsApp.
 */
export const WHATSAPP_MODULES = ["whatsapp.business"] as const;

export type WhatsAppModule = (typeof WHATSAPP_MODULES)[number];

/**
 * Commerce Store module — the B2B wholesale storefront add-on. A single gate
 * string (`commerce.admin`) that unlocks the Store admin module inside the web
 * app (products, buyers, orders, quotes, samples). The public storefront
 * (`apps/ecom-store`) and buyer auth land in later milestones and will add
 * their own strings when they do. @see docs/ecom/prd.md
 */
export const COMMERCE_MODULES = ["commerce.admin"] as const;

export type CommerceModule = (typeof COMMERCE_MODULES)[number];

/**
 * 1.0-withheld modules — full ERP contexts that EXIST in the codebase but are
 * hidden from the default Flint 1.0 (Garment OS) surface and return as full
 * modules in 1.1/1.2. Registered here so the three gate layers — nav
 * (`nav-config.ts`), route (`requireModule`), and server
 * (`entitledProcedureFor`) — reference a canonical, typed string.
 *
 * Each is exposed as a FREE, default-OFF add-on (`ADDON_CATALOG` in `addons.ts`
 * / `ADDON_MODULES` in the domain, ADR-0046): a normally-onboarded workspace
 * never has them (`installDefaultAddons` leaves them off → the 1.0 surface is
 * the default), but an Owner can switch one on per workspace in Settings →
 * Subscription for demos, which adds its string to `company_entitlements.modules`
 * and opens all three gates. @see docs/product/v1.0-release-plan.md §2–§3,
 * docs/adr/0046-withheld-1.0-modules.md
 *
 * Note `cash` and `stock-ops` are partial gates: bank accounts stays in the
 * 1.0 Cash surface, and stock/warehouses/adjustments stay in Inventory — only
 * petty-cash/transfers/reconcile and stock-transfers sit behind these strings.
 */
export const WITHHELD_MODULES = [
  "payroll",
  "documents",
  "reports",
  "books",
  "cash",
  "stock-ops",
  "design-studio",
] as const;

export type WithheldModule = (typeof WITHHELD_MODULES)[number];

/**
 * Every entitle-able module string — the union of all add-on bundles plus the
 * 1.0-withheld contexts. Future add-ons append their own. Kept as a distinct
 * type so the gate's surface reads as "a module" rather than any single add-on's
 * module.
 */
export type AppModule =
  | GarmentModule
  | PosModule
  | AiModule
  | WhatsAppModule
  | WithheldModule
  | CommerceModule;

const ALL_MODULES = [
  ...GARMENT_MODULES,
  ...POS_MODULES,
  ...AI_MODULES,
  ...WHATSAPP_MODULES,
  ...COMMERCE_MODULES,
  ...WITHHELD_MODULES,
] as const;

/** Zod enum over the known modules — for validating module params on the wire. */
export const appModuleSchema = z.enum(ALL_MODULES);

/**
 * Pure entitlement predicate — does this company's module list unlock `module`?
 * The single source of the gate's decision; `entitledProcedureFor` calls it,
 * and it's unit-tested in isolation so the gate's logic isn't only covered by
 * integration tests.
 */
export function isModuleEntitled(modules: readonly string[], module: AppModule): boolean {
  return modules.includes(module);
}
